import React, { useContext } from 'react';
import 'react-dates/lib/css/_datepicker.css';
import Form from '../../../components/form/Form';
import {
  chPerm,
  conditionalList,
  deleteForm,
  errorInput,
  formInput,
  searchSelectProps,
} from '../../../components/helpers';
import { Input, Select, Modal, Button, Checkbox } from 'semantic-ui-react';
import moment from 'moment';
import membersForms from '../../../services/api/membersForms';
import Swal from 'sweetalert2';
import churchForms from '../../../services/api/churchForms';
import { RegistrationForm } from 'src/assets/styles/components/Registration';
import { ListsContext } from '../../../services/contextProviders/lists/listsContext';
import { uploadDocumentsInput } from 'src/containers/volunteer/VolunteerForm';
import { useEffect } from 'react';
import {
  isCaptchaRequired,
  useRecaptchaToken,
  RECAPTCHA_SECURITY_ERROR,
} from 'src/components/helpers/recaptchaUtils';

const VolunteerForm = (props) => {
  const {
    getInfo,
    loading,
    mainInputs,
    set_loading,
    set_showVolunteerForm,
    prevVolunteerInputs = {},
  } = props;
  const [inputs, setInputs] = React.useState({});
  const [errorInputs, set_errorInputs] = React.useState({});
  const [duplicate, set_duplicate] = React.useState(false);
  const [image, set_image] = React.useState(null);
  const [documentSideA, set_documentSideA] = React.useState(null);
  const [documentSideB, set_documentSideB] = React.useState(null);
  const [documentSideAPreview, set_documentSideAPreview] = React.useState(null);
  const [documentSideBPreview, set_documentSideBPreview] = React.useState(null);
  const getCaptchaToken = useRecaptchaToken('volunteer_form');
  const editingVolunteerId = React.useRef(
    prevVolunteerInputs?.id && prevVolunteerInputs?.guest_id != null ? prevVolunteerInputs.id : null
  );
  const editingGuestId = React.useRef(prevVolunteerInputs?.guest_id || null);
  const isRecovering = Boolean(prevVolunteerInputs?.deleted || prevVolunteerInputs?.deletedReason);

  useEffect(() => {
    setInputs({
      ...prevVolunteerInputs,
      ...(prevVolunteerInputs?.emergency_contact || {}),
      section: prevVolunteerInputs?.section || mainInputs?.section,
      serviceArea: prevVolunteerInputs?.serviceArea || mainInputs?.serviceArea,
    });
  }, []);

  const { churchMinistries, experienceVolunteersSections, experienceVolunteersRoles } =
    useContext(ListsContext);

  const sectionIsFundaciones = (sectionKey) => {
    if (!sectionKey) return false;
    const option = experienceVolunteersSections.find((opt) => opt.key === sectionKey);
    const content = `${option?.name || ''} ${option?.key || sectionKey}`.replace(/_/g, ' ');
    return /fundacion/i.test(content);
  };

  let age = (birthdate) => moment().diff(birthdate, 'years');

  const handleCheckers = (name) => setInputs({ ...inputs, [name]: !inputs[name] });

  const verifyForm = () => {
    let mandatoryInputs = [
      'name',
      'lastName',
      'email',
      'gender',
      'birthdate',
      'identification',
      'phone',
      'section',
      'role',
      'serviceArea',
    ];

    if (age(inputs.birthdate) <= 14) {
      mandatoryInputs = [...mandatoryInputs, 'parentIdentification', 'parentFullname'];
    }

    if (editingVolunteerId.current && sectionIsFundaciones(inputs.section)) {
      mandatoryInputs = [...mandatoryInputs, 'subsection'];
    }

    let errorInputs = {};

    mandatoryInputs.forEach((input) => {
      const value = input === 'subsection' ? inputs.subsection?.trim() : inputs[input];
      errorInputs = errorInput({
        errorInputs,
        input: { name: input, value },
      });
    });

    if (inputs?.identification && inputs?.identification?.length <= 5) {
      errorInputs = errorInput({
        errorInputs,
        input: {
          name: 'identification',
          value: inputs?.identification.length >= 5,
        },
      });
    }

    if (inputs?.phone && inputs?.phone?.length <= 10) {
      errorInputs = errorInput({
        errorInputs,
        input: {
          name: 'phone',
          value: inputs?.phone.length >= 10,
        },
      });
    }

    set_errorInputs(errorInputs);
    return Object.values(errorInputs)?.includes(true);
  };

  const nextStep = async (e) => {
    e.preventDefault();
    if (verifyForm()) return;
    set_loading(true);

    const captchaRequired = isCaptchaRequired();
    const captcha = await getCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      set_loading(false);
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return;
    }

    const data = new FormData();
    const birthdate = inputs.birthdate ? moment(inputs.birthdate).format('YYYY-MM-DD') : '';
    const guestId = parseInt(editingGuestId.current, 10);

    if (!duplicate && editingVolunteerId.current) data.append('id', editingVolunteerId.current);
    if (Number.isInteger(guestId)) data.append('guest_id', String(guestId));
    if (birthdate && birthdate !== 'Invalid date') data.append('birthdate', birthdate);
    if (documentSideA) data.append('documentSideA', documentSideA);
    if (documentSideB) data.append('documentSideB', documentSideB);
    data.append('email', inputs.email);
    data.append('gender', inputs.gender);
    data.append('identification', String(inputs.identification));
    data.append('lastName', inputs.lastName);
    data.append('name', inputs.name);
    data.append('ocupation', inputs.ocupation || '');
    data.append('phone', inputs.phone);
    if (image) data.append('profile', image);
    data.append('role', inputs.role);
    data.append('section', inputs.section);
    data.append('subsection', sectionIsFundaciones(inputs.section) ? inputs.subsection || '' : '');
    data.append('serviceArea', inputs.serviceArea);
    data.append('suspended', !!inputs.suspended);
    data.append('convenio', !!inputs.convenio);
    data.append('tshirtsize', inputs.tshirtsize || '');
    data.append('emergencyContactName', inputs.emergencyContactName || '');
    data.append('emergencyContactPhone', inputs.emergencyContactPhone || '');
    data.append('emergencyContactRelation', inputs.emergencyContactRelation || '');
    if (captchaRequired) data.append('captcha', captcha);

    churchForms
      .volunteerForm(data)
      .then((response) => {
        set_loading(false);
        if (response.data.success) {
          Swal.fire(response.data.message);
          set_showVolunteerForm(null);
          getInfo?.();
        } else if (response.data.message) {
          Swal.fire(response.data.message);
        }
      })
      .catch((error) => {
        set_loading(false);
        if (error && error.response.data) {
          Swal.fire(error.response.data.message);
        }
      });
  };

  const handleInputs = (event) => {
    let value = event.target.value;
    let name = event.target.name;
    setInputs({ ...inputs, [name]: value });
  };

  const handleDropDown = ({ name, value }) => {
    setInputs({ ...inputs, [name]: value });
  };

  const selectHandle = (_event, { value, name }) => {
    setInputs((prev) =>
      name === 'section' && !sectionIsFundaciones(value)
        ? { ...prev, section: value, subsection: '' }
        : { ...prev, [name]: value }
    );
  };

  const loadMember = ({ identification, phone, keepPhoto = true } = {}) => {
    if (!identification || !phone) return Swal.fire('Para buscar escribe la cédula y el celular');

    set_loading(true);

    membersForms
      .getAMember({ identification, phone })
      .then((response) => {
        if (response.data.success) {
          set_loading(false);
          if (response.data.finded && response.data.match) {
            const guest = { ...(response.data.guest || {}) };
            const foundGuestId = guest.id;
            const emergencyContact = guest.emergency_contact || {};
            const photo = guest.photo;
            delete guest.id;
            delete guest.photo;
            delete guest.emergency_contact;
            if (foundGuestId) editingGuestId.current = foundGuestId;
            setInputs((prev) => ({
              ...prev,
              ...guest,
              ...emergencyContact,
              identification,
              phone,
              id: prev.id,
              guest_id: foundGuestId || prev.guest_id,
              photo: keepPhoto ? prev.photo : photo || prev.photo,
            }));
          } else {
            if (response.data.message) Swal.fire(response.data.message);
          }
        }
      })
      .catch((error) => {
        set_loading(false);
        if (error.response) Swal.fire(error.response.data.message);
      });
  };

  const getAMember = (e) => {
    e.preventDefault();
    loadMember({ identification: inputs.identification, phone: inputs.phone });
  };

  const deleteVolunteer = () => {
    return (
      <Button
        color="red"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          deleteForm({
            subtitle: inputs.name,
            Swal,
            func: async ({ text, successAlert }) => {
              await churchForms
                .deleteVolunteerForm({
                  guest_id: editingGuestId.current || inputs.guest_id,
                  id: editingVolunteerId.current || inputs.id,
                  deleted: text,
                })
                .then((response) => {
                  if (response.data.success) {
                    successAlert();
                    getInfo();
                    set_showVolunteerForm(null);
                  } else {
                    Swal.fire(response.data.message);
                  }
                })
                .catch((error) => {
                  if (error.response) Swal.fire(error.response.data.message);
                });
            },
          });
        }}
      >
        Eliminar
      </Button>
    );
  };

  const moreInfo = () => {
    return (
      <>
        <div className="selectorCheck" style={{ margin: '25px 0' }}>
          <label
            style={{
              cursor: 'pointer',
              fontWeight: inputs.suspended ? 'normal' : 'bold',
              padding: '0 10px',
            }}
            htmlFor="suspended"
          >
            Activo
          </label>
          <Checkbox
            label="Suspendido"
            name="suspended"
            style={{ fontWeight: inputs.suspended ? 'bold' : 'normal' }}
            id="suspended"
            checked={inputs.suspended}
            toggle
            className={`${'suspended'} ${errorInputs['suspended'] ? 'error' : ''}`}
            onClick={() => handleCheckers('suspended')}
          />
        </div>
        <div>
          <p style={{ margin: '0' }}>Talla de camisa</p>
          {formInput({
            Input,
            handler: handleInputs,
            label: 'Talla de camisa',
            name: 'tshirtsize',
            value: inputs.tshirtsize || '',
            errorInputs,
          })}
        </div>
        <div>
          <p style={{ margin: 0, color: errorInputs?.serviceArea ? 'red' : '' }}>Area de servcio</p>
          <Select
            placeholder={'Area de servcio'}
            className={errorInputs?.serviceArea ? 'error' : ''}
            style={{ margin: '0 0 20px 0' }}
            disabled={!chPerm(['volunteers'])}
            {...searchSelectProps({
              name: 'serviceArea',
              handler: selectHandle,
              value: inputs?.serviceArea,
              options: churchMinistries.map((opt) => ({
                key: opt?.key,
                value: opt?.key,
                text: opt?.name,
              })),
            })}
          />
          <p style={{ margin: 0, color: errorInputs?.role ? 'red' : '' }}>Rol del voluntario</p>
          <Select
            placeholder={'Rol del voluntario'}
            className={errorInputs?.role ? 'error' : ''}
            style={{ margin: '0 0 20px 0' }}
            disabled={!chPerm(['volunteers']) && mainInputs?.role !== 'Coordinador'}
            {...searchSelectProps({
              name: 'role',
              handler: selectHandle,
              value: inputs?.role,
              options: [
                ...(chPerm(['volunteers'])
                  ? experienceVolunteersRoles.map((role) => role.key)
                  : []),
                ...(mainInputs?.role === 'Coordinador'
                  ? ['Encargado', 'Encargado_jr', 'Voluntario']
                  : []),
              ].map((a) => ({ key: a, value: a, text: a?.replace(/_/g, ' ') })),
            })}
          />
          <p style={{ margin: 0, color: errorInputs?.section ? 'red' : '' }}>Seccion</p>
          <Select
            placeholder={'Seccion'}
            className={errorInputs?.section ? 'error' : ''}
            style={{ margin: '0 0 20px 0' }}
            disabled={!chPerm(['volunteers']) && mainInputs?.role !== 'Coordinador'}
            {...searchSelectProps({
              name: 'section',
              handler: selectHandle,
              value: inputs?.section,
              options: [
                ...(chPerm(['volunteers'])
                  ? [{ key: 'Pendientes', value: 'Pendientes', text: 'Pendientes' }]
                  : []),
                ...conditionalList(experienceVolunteersSections, inputs?.serviceArea).map(
                  (opt) => ({
                    key: opt?.key,
                    value: opt?.key,
                    text: opt?.name?.replace(/_/g, ' '),
                  })
                ),
              ],
            })}
          />
          {sectionIsFundaciones(inputs?.section) && (
            <div>
              <p style={{ margin: '0' }}>Subsección</p>
              {formInput({
                Input,
                handler: handleInputs,
                label: 'Subsección',
                name: 'subsection',
                value: inputs.subsection || '',
                errorInputs,
              })}
            </div>
          )}
          <div className="selectorCheck" style={{ margin: '25px 0' }}>
            <label
              style={{
                cursor: 'pointer',
                fontWeight: inputs.convenio ? 'normal' : 'bold',
                padding: '0 10px',
              }}
              htmlFor="convenio"
            >
              Convenio pendiente
            </label>
            <Checkbox
              label="Convenio listo"
              name="convenio"
              style={{ fontWeight: inputs.convenio ? 'bold' : 'normal' }}
              id="convenio"
              checked={inputs.convenio}
              toggle
              className={`${'convenio'} ${errorInputs['convenio'] ? 'error' : ''}`}
              onClick={() => handleCheckers('convenio')}
            />
          </div>
          <div>
            {uploadDocumentsInput({
              formID: 'documentSideA',
              secString: 'el lado frontal del documento',
              varFile: documentSideA,
              filling: true,
              inputs,
              set_documentSideA,
              errorInputs,
              image,
              set_imagePreview: set_documentSideAPreview,
            })}
            <div>
              {!!(inputs.documentSideA || documentSideAPreview) && (
                <img
                  src={inputs.documentSideA || documentSideAPreview}
                  alt="documentSideA"
                  style={{ width: '200px' }}
                />
              )}
            </div>
            {uploadDocumentsInput({
              formID: 'documentSideB',
              secString: 'el lado de atrás del documento',
              varFile: documentSideB,
              set_documentSideB,
              filling: true,
              inputs,
              errorInputs,
              image,
              set_imagePreview: set_documentSideBPreview,
            })}
            <div>
              {!!(inputs.documentSideB || documentSideBPreview) && (
                <img
                  src={inputs.documentSideB || documentSideBPreview}
                  alt="documentSideB"
                  style={{ width: '200px' }}
                />
              )}
            </div>
          </div>
          <div>{!!inputs.id && !duplicate && !isRecovering && deleteVolunteer()}</div>
          <div>
            {!!inputs.id && !duplicate && !isRecovering && chPerm(['volunteers']) && (
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  set_duplicate(true);
                }}
                color="black"
              >
                Duplicar
              </Button>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <Modal open onClose={() => set_showVolunteerForm(null)}>
      <Modal.Content>
        <Modal.Description>
          <RegistrationForm onSubmit={(e) => nextStep(e)}>
            <h2>
              {isRecovering
                ? 'Recuperar voluntario'
                : !!inputs.id
                ? 'Modificar los datos del voluntario'
                : 'Llena los datos del voluntario'}
            </h2>
            {isRecovering && (
              <p style={{ marginTop: 0 }}>
                Revisa o ajusta la información y pulsa Recuperar para volver a activar este
                voluntario.
              </p>
            )}
            {Object.values(errorInputs)?.includes(true) && (
              <h4 style={{ color: 'red' }}>Por favor verifica los campos en rojo.</h4>
            )}
            <div>
              <Button loading={loading} disabled={loading} onClick={(e) => getAMember(e)}>
                Llenar información
              </Button>
              <p style={{ fontSize: '12px', margin: 0 }}>
                Ingresa el celular y cédula para traer la información que hay en el sistema
                asociados.
              </p>
            </div>
            {!!(age(inputs.birthdate) <= 14 && inputs.birthdate) && (
              <div>
                <p style={{ margin: '0' }}>Información para menores de 14 años</p>
                {formInput({
                  Input,
                  handler: handleInputs,
                  label: 'Nombre del acudiente',
                  name: 'parentFullname',
                  value: inputs.parentFullname || '',
                  errorInputs,
                })}
                {formInput({
                  Input,
                  handler: handleInputs,
                  label: 'Cédula del acudiente',
                  name: 'parentIdentification',
                  value: inputs.parentIdentification || '',
                  errorInputs,
                  type: 'number',
                })}
              </div>
            )}
            <Form
              saveTag={isRecovering ? 'Recuperar' : duplicate ? 'Duplicar' : 'Guardar'}
              Attendant={{ ...inputs, id: editingGuestId.current || inputs.guest_id }}
              userprofile={true}
              handleInputs={handleInputs}
              handleDropDown={handleDropDown}
              errorInputs={errorInputs}
              showEmergencyContact
              sendInForm
              showOcupation
              showAge
              showGender
              moreInfo={moreInfo}
              showBirthdate
              loadedData={loading}
              image={image}
              set_image={set_image}
              submit={(e) => nextStep(e)}
              setAttendant={(updater) => {
                setInputs((prev) => {
                  const guestView = { ...prev, id: editingGuestId.current || prev.guest_id };
                  const next =
                    typeof updater === 'function' ? updater(guestView) : { ...prev, ...updater };
                  return {
                    ...prev,
                    ...next,
                    id: prev.id,
                    guest_id: editingGuestId.current || prev.guest_id || next.guest_id,
                  };
                });
              }}
              setErrorInputs={set_errorInputs}
              onGuestUpdated={({ identification, phone }) =>
                loadMember({ identification, phone, keepPhoto: false })
              }
            />
            <div>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  set_showVolunteerForm(null);
                }}
              >
                Cancelar
              </Button>
            </div>
          </RegistrationForm>
        </Modal.Description>
      </Modal.Content>
    </Modal>
  );
};

export default VolunteerForm;
